<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useBreakpoint } from "@/design/composables/use-breakpoint";
import { usePaneSplit } from "@/design/composables/use-pane-split";
import Icon from "@/design/primitives/Icon.vue";
import type { OrbState } from "@/shared/agent-display/BenOrb.vue";
import PreviewAsClient from "@/shared/agent-display/PreviewAsClient.vue";
import type { PendingAudio, PendingFile, PendingImage } from "../api/attachments";
import type { TurnAttachment } from "../api/turn-attachments";
import {
	type ChatAgent,
	loadSessionMessages,
	type SessionSubject,
	useCreateSession,
	useDeleteMessage,
} from "../api/use-agents";
import { rememberedBubble } from "../composables/bubble";
import { documentPaths, OPEN_CHAT_FILE } from "../composables/chat-document";
import { useBackgroundAgents } from "../composables/use-background-agents";
import {
	type NavigateTarget,
	type PreparedDraft,
	useTurnStream,
} from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import ChatBubble from "./ChatBubble.vue";
import ChatComposer from "./ChatComposer.vue";
import ChatDocumentPanel from "./ChatDocumentPanel.vue";
import ChatSuggestions from "./ChatSuggestions.vue";
import LiveTurn from "./LiveTurn.vue";

/**
 * Conversar com um agente, ao vivo.
 *
 * O que sobrou depois da fatia (o arquivo tinha 812 linhas) é a TELA: a sessão
 * que abre na primeira mensagem, a rolagem que segue sozinha, e o que fazer
 * quando a pessoa pede de novo. O turno mora em `use-turn-stream.ts`.
 *
 * `subject` é o que ancora a conversa a alguma coisa, sem um segundo componente:
 * a sessão que ele abre é SOBRE a conversa da tela, reabri-la reabre a thread, e
 * todo turno leva o id para o agente saber do que se fala. Quem só o entrega
 * depois de o SERVIDOR ter dito que esta é a porta da conversa é a doca — ver
 * `BenDock.vue`, que explica por que a tela não pode decidir isso sozinha.
 */
const props = defineProps<{
	agent: ChatAgent;
	sessionId?: string | null;
	compact?: boolean;
	wide?: boolean;
	subject?: SessionSubject | null;
	/** Deixa este chat navegar sozinho. Falso por padrão: quem monta a conversa é
	 *  quem sabe se mudar de rota desmonta a própria conversa (contrato com B). */
	autoNavigate?: boolean;
}>();

const emit = defineEmits<{
	preview: [boolean];
	sessionStarted: [string];
	state: [OrbState];
	/** O texto pronto para o rascunho da conversa (contrato com o lote D). */
	useAsDraft: [text: string];
	/**
	 * O BEN PREPAROU uma resposta — palavras e, se houver, o arquivo.
	 *
	 * Irmão de `useAsDraft` e não o mesmo evento: aquele é um CLIQUE da pessoa
	 * numa bolha que já existe, e leva só texto; este chega sozinho, no meio do
	 * turno, e leva os ids dos arquivos que o compositor tem de resolver. Quem os
	 * liga ao compositor é a página do inbox — este componente não conhece
	 * `features/inbox` e não deve conhecer.
	 */
	preparedDraft: [draft: PreparedDraft];
	/** Aonde o servidor mandou ir. Quem chama o router é a doca. */
	navigate: [target: NavigateTarget];
}>();

const { t } = useAgentsI18n();
const composerRef = ref<{
	prefill: (text: string, send?: boolean, append?: boolean) => void;
} | null>(null);

/** O gancho do click-to-edit do site: escreve no compositor sem enviar. */
defineExpose({
	prefill: (text: string, send?: boolean) => composerRef.value?.prefill(text, send),
});
const router = useRouter();
const createSession = useCreateSession();
const session = ref<string | null>(props.sessionId ?? null);

/**
 * A conversa que já está DESENHADA nesta tela.
 *
 * Não é `session.value`: aquela é a conversa em que se escreve, e as duas
 * separam-se no primeiro envio. Ver o guarda do watcher abaixo — é a diferença
 * entre a pergunta ficar na tela e desaparecer debaixo de quem a escreveu.
 *
 * `undefined` é "esta tela ainda não carregou thread nenhuma", que não é o mesmo
 * que `null` ("está numa conversa nova, e é para ficar vazia").
 */
let shown: string | null | undefined;

/**
 * Passa a escrever NESTA conversa, sem recarregar o que já está na tela.
 *
 * Chamado por quem a abriu (o `submit`) e pelo servidor, que a abre sozinho
 * quando o cliente não conseguiu (quadro `session`). Nos dois casos a thread da
 * tela é a boa: o turno está a acontecer aqui.
 */
function adopt(id: string): void {
	if (session.value === id) return;
	session.value = id;
	shown = id;
	emit("sessionStarted", id);
}

const {
	bubbles,
	streaming,
	liveText,
	liveThinking,
	liveTools,
	liveWriting,
	liveDocument,
	savedDocuments,
	failure,
	notice,
	lost,
	lookAgain,
	nextSuggestions,
	state,
	send,
	stop,
	resume,
	queued,
	enqueue,
} = useTurnStream({
	agent: () => props.agent.code,
	sessionId: () => session.value,
	// O que o turno é SOBRE, quando a thread está ancorada: o servidor põe o turno
	// na trilha da conversa e ainda arquiva a pergunta do agente na SESSÃO.
	conversationId: () => (props.subject?.kind === "conversation" ? props.subject.id : null),
	onNavigate: (target) => {
		emit("navigate", target);
		if (props.autoNavigate) void router.push(target.to);
	},
	// O servidor arquivou este turno numa conversa que ele próprio abriu.
	onSession: (id) => adopt(id),
	onPreparedDraft: (draft) => emit("preparedDraft", draft),
	onFrame: () => void scrollDown(),
});

watch(state, (value) => emit("state", value), { immediate: true });

// O Ben acordado por um subagente que acabou: a tela liga-se ao turno que ninguém pediu.
useBackgroundAgents({ bubbles, streaming, resume });

/** Uma thread longa custa tokens em todo turno, porque ela sobe inteira. */
const longThread = computed(() => bubbles.value.length >= 24);
/** Só dentro de uma conversa há para onde mandar o texto em um clique. */
const canDraft = computed(() => props.subject?.kind === "conversation");

/**
 * TROCAR DE CONVERSA — e só isso.
 *
 * ── O DEFEITO QUE O GUARDA DESFAZ ─────────────────────────────────────────
 *
 * No primeiro envio, `submit` abre a sessão, avisa a doca (`sessionStarted`), e a
 * doca escreve o `sessionId` de volta como propriedade. Este watcher disparava
 * nessa volta, zerava `bubbles` com o turno EM VOO e sobrescrevia a thread com o
 * que o servidor tivesse naquele milissegundo — a pergunta que a pessoa acabou de
 * escrever desaparecia à frente dela. Era o "a mensagem some" do dono.
 *
 * `shown` diz o que já está na tela. Quando a propriedade só ecoa a conversa que
 * esta tela abriu, não há nada a carregar: a thread boa é a que está aqui.
 */
watch(
	() => props.sessionId,
	async (id) => {
		session.value = id ?? null;
		if (shown === (id ?? null)) return;
		shown = id ?? null;
		bubbles.value = [];
		if (!id) return;
		// Reabrir uma conversa mostra o que ela FEZ, não só o que ela disse — os
		// passos, as imagens e os arquivos entregues voltam com as mensagens, e quem
		// os traduz em fala é o `bubble.ts`, o mesmo que traduz o fio.
		const messages = await loadSessionMessages(id).catch(() => []);
		bubbles.value = messages.map(rememberedBubble);
		await scrollDown();
		// E SÓ AGORA: se um turno ficou a correr enquanto ninguém via — a aba
		// fechou, o telemóvel trocou de app, a rota mudou —, ele continua vivo no
		// servidor e volta para o fim desta thread. Antes das falas carregarem, a
		// resposta cairia numa conversa ainda vazia.
		void resume();
	},
	{ immediate: true },
);

/** Se a vista está grudada no fim. A maior diferença entre um chat que transmite
 *  e é agradável e um que não é: quem subiu para reler uma resposta e é jogado de
 *  volta a cada delta não consegue ler. A vista só segue quem já está embaixo. */
const { isMobile } = useBreakpoint();
const previewOpen = ref(false);
const previewFile = ref<TurnAttachment | null>(null);
const previewPath = ref<string | null>(null);
const documents = computed(() => [
	...new Set([
		...documentPaths(bubbles.value.flatMap((b) => b.steps ?? [])),
		...Object.keys(savedDocuments.value),
	]),
]);
const followDocument = ref(true);
const documentSplit = usePaneSplit({
	storageKey: "benvox.chat.document-width",
	min: 320,
	maxFraction: 0.75,
	anchor: "right",
});
const splitPreview = computed(() => props.wide && !isMobile.value);
provide(OPEN_CHAT_FILE, (file) => {
	previewPath.value = null;
	previewFile.value = file;
	previewOpen.value = true;
	followDocument.value = false;
});
watch(previewOpen, (open) => emit("preview", open));
onBeforeUnmount(() => emit("preview", false));
watch(liveDocument, (document, previous) => {
	if (!document) {
		if (!previewFile.value && !previewPath.value) previewOpen.value = false;
		return;
	}
	if (document.name !== previous?.name && !previewOpen.value) followDocument.value = true;
	if (followDocument.value) {
		previewFile.value = null;
		previewPath.value = null;
		if (props.wide && !isMobile.value) previewOpen.value = true;
	}
});
function closePreview(): void {
	previewOpen.value = false;
	followDocument.value = false;
}
function openLiveDocument(): void {
	previewPath.value = null;
	previewFile.value = null;
	previewOpen.value = true;
	followDocument.value = true;
}

function openDocument(path: string): void {
	previewFile.value = null;
	previewPath.value = path;
	previewOpen.value = true;
	followDocument.value = false;
}
watch(savedDocuments, (saved, before) => {
	const path = Object.keys(saved)
		.reverse()
		.find((path) => saved[path] !== before[path]);
	if (path && followDocument.value && props.wide && !isMobile.value) {
		openDocument(path);
		followDocument.value = true;
	}
});
function reviseDocument(text: string): void {
	if (!splitPreview.value) closePreview();
	composerRef.value?.prefill(text, false, true);
}

const BOTTOM_SLACK = 60;
const scroller = ref<HTMLElement | null>(null);
const atBottom = ref(true);

function onScroll(): void {
	const el = scroller.value;
	if (!el) return;
	atBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK;
}

async function scrollDown(force = false): Promise<void> {
	if (!force && !atBottom.value) return;
	await nextTick();
	const element = scroller.value;
	if (!element) return;
	element.scrollTop = element.scrollHeight;
	atBottom.value = true;
}

/** O que foi anexado no último envio, para o `regerar` mandar o mesmo turno. */
let lastImages: PendingImage[] = [];
/** E os arquivos — já guardados, o `regerar` só volta a referi-los pelo id. */
let lastFiles: PendingFile[] = [];

async function submit(payload: {
	text: string;
	images: PendingImage[];
	audio?: PendingAudio;
	files?: PendingFile[];
}): Promise<void> {
	// A conversa é aberta na PRIMEIRA mensagem, não quando o painel abre: senão
	// toda visita deixaria uma thread vazia na lateral.
	if (!session.value) {
		const created = await createSession
			.mutateAsync({ agent: props.agent.code, subject: props.subject ?? null })
			.catch(() => null);
		if (created) adopt(created.id);
		// Falhou abrir a conversa? O turno vai na mesma: o SERVIDOR abre-a e diz
		// qual é pelo quadro `session` (ver `onSession`). Antes, este caso corria um
		// turno que nunca chegava a ser gravado — e reabrir a janela não mostrava nada.
	}
	// Forçado: acabou de mandar, quer ver chegar.
	atBottom.value = true;
	lastImages = payload.images;
	lastFiles = payload.files ?? [];
	await send(payload.text, payload.images, payload.audio, payload.files ?? []);
}

/**
 * Pedir de novo, e corrigir o que foi perguntado.
 *
 * ── O QUE ACONTECE COM O PAR JÁ GRAVADO ───────────────────────────────────
 *
 * O servidor grava a mensagem da pessoa ANTES do turno
 * (`agent-run.controller.ts:144-157`) e a resposta depois (`:210-219`), então
 * reenviar grava um SEGUNDO par em `agent_message`: na tela a tentativa velha
 * some na hora, mas depois de um F5 a conversa mostra as duas. Não há flag de
 * "substitua o último par", e inventar rota para apagá-lo não é desta fase — a
 * escolha é ACEITAR: o gravado é o que de fato aconteceu (a pessoa perguntou
 * duas vezes) e o que ela lê enquanto conversa é a versão boa.
 */
function regenerate(): void {
	const all = bubbles.value;
	let i = all.length - 1;
	while (i >= 0 && all[i]?.role !== "user") i--;
	const asked = all[i];
	if (!asked) return;
	bubbles.value = all.slice(0, i);
	void submit({ text: asked.text, images: lastImages, files: lastFiles });
}

/** Corta a conversa a partir da fala corrigida e manda de novo. As imagens não
 *  voltam: os anexos foram consumidos no envio, e reanexar é um clique honesto. */
function resend(index: number, text: string): void {
	bubbles.value = bubbles.value.slice(0, index);
	void submit({ text, images: [] });
}

const deleteMessage = useDeleteMessage();

/**
 * O lixinho da barra (padrão V2): some da tela na hora, e o servidor apaga
 * atrás. Se ele recusar, a fala volta no próximo F5 — o custo certo para um
 * gesto pequeno; um spinner por fala apagada seria cerimónia.
 */
function removeMessage(messageId: string): void {
	const sessionId = session.value;
	if (!sessionId) return;
	bubbles.value = bubbles.value.filter((bubble) => bubble.id !== messageId);
	deleteMessage.mutate({ sessionId, messageId });
}
</script>

<template>
	<div class="flex h-full min-h-0 min-w-0" :class="documentSplit.resizing.value ? 'select-none' : ''">
	<div v-show="!previewOpen || splitPreview" class="relative flex min-h-0 min-w-0 flex-1 flex-col" :class="wide && !previewOpen ? 'mx-auto max-w-4xl' : ''">
		<!-- O afford de ADMIN, na moldura e nunca dentro do cartão que ele desliga.
		     Ver `PreviewAsClient.vue`: quem simula um cliente tem de poder voltar. -->
		<PreviewAsClient compact class="absolute top-[calc(var(--dock-top,0px)+0.5rem)] right-3 z-20" />

		<div
			ref="scroller"
			class="min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-4 pt-[calc(var(--dock-top,0px)+0.75rem)] pb-3 [overflow-wrap:anywhere]"
			@scroll.passive="onScroll"
		>
			<!-- A chave leva a HORA da fala, e não só o índice: cortar a thread (regerar,
			     reenviar) reaproveitava as instâncias por posição, e o rascunho de edição
			     ou o painel do 👎 migravam para outra fala. -->
			<ChatBubble
				v-for="(bubble, index) in bubbles"
				:key="`${index}:${bubble.role}:${bubble.at ?? 0}`"
				:bubble="bubble"
				:last="index === bubbles.length - 1 && !streaming"
				:busy="streaming"
				:can-draft="canDraft"
				:can-remove="!!session"
				@use-as-draft="emit('useAsDraft', $event)"
				@regenerate="regenerate"
				@resend="resend(index, $event)"
				@remove="removeMessage"
			/>

			<div v-if="documents.length" class="flex flex-wrap gap-2">
<button v-for="path in documents" :key="path" type="button" class="max-w-full truncate rounded-lg border border-border px-3 py-2 text-xs text-accent" :title="path" @click="openDocument(path)">{{ path.split('/').pop() }}</button>
</div>
			<button v-if="liveDocument && !documents.includes(liveDocument.name)" type="button" class="flex w-full items-center gap-2 rounded-lg border border-border bg-bg-subtle p-3 text-left text-sm text-accent" @click="openLiveDocument">
				<span class="min-w-0 flex-1 truncate">{{ liveDocument.name }}</span><span>{{ t("document.open") }}</span>
			</button>
			<LiveTurn
				v-if="streaming"
				:tools="liveTools"
				:text="liveText"
				:thinking="liveThinking"
				:writing="liveDocument?.status === 'writing' ? null : liveWriting"
			/>

			<!-- Embaixo da última resposta, e não só na conversa vazia. O rosto do
			     agente vai junto: a tela vazia é a primeira coisa que se vê ao abrir
			     o Ben, e quem abre precisa saber com quem está falando. -->
			<ChatSuggestions
				v-if="!streaming"
				:mode="agent.mode"
				:avatar-url="agent.avatarUrl"
				:in-conversation="!!subject"
				:started="bubbles.length > 0"
				:next="nextSuggestions"
				@use="submit({ text: $event, images: [] })"
			/>
		</div>

		<!-- Oferecido só quando faz falta: um botão permanente é mais uma coisa na
		     tela que não diz nada na maior parte do tempo. -->
		<button
			v-if="!atBottom"
			type="button"
			class="relative z-20 mx-auto -mt-12 mb-1 flex size-10 items-center justify-center rounded-full border border-border bg-surface text-fg shadow-md"
			:aria-label="t('chat.toEnd')"
			:title="t('chat.toEnd')"
			@click="scrollDown(true)"
		>
			<Icon name="arrowDown" class="size-5" />
		</button>

		<!-- A reconciliação falando baixo: a conexão caiu, e o que a tela fez sobre isso. -->
		<p v-if="notice" class="shrink-0 px-3 py-1 text-center text-2xs text-fg-subtle">
			{{ notice }}
		</p>

		<!-- A SAÍDA DO BECO. A busca esgotou-se, mas a resposta pode existir: o
		     servidor arquiva-a oiça alguém ou não. Este botão procura outra vez —
		     reata o turno se ele ainda correr, relê a thread se ele já acabou — e
		     não gasta turno nenhum. Era isto que faltava quando a única coisa
		     oferecida era "mande de novo", e mandar de novo custou o turno duas
		     vezes (o relato do dono, 06/09). -->
		<div v-if="lost" class="shrink-0 px-3 pb-1 text-center">
			<button
				type="button"
				class="rounded-full border border-border px-3 py-1 text-2xs text-fg-muted hover:bg-surface-muted"
				@click="lookAgain()"
			>
				{{ t("recovery.seeTurn") }}
			</button>
		</div>

		<p
			v-if="longThread"
			class="shrink-0 border-t border-border px-3 py-1.5 text-center text-2xs text-fg-subtle"
		>
			{{ t("chat.longThread") }}
		</p>

		<!-- As mensagens que aguardam a próxima rodada: sem esta lista,
		     "Juntar" apaga o que foi escrito e não mostra para onde foi. -->
		<div v-if="queued.length" class="max-h-32 shrink-0 overflow-y-auto px-3 pb-1 text-2xs text-fg-subtle" aria-live="polite">
			<p v-for="message in queued" :key="message.id" class="whitespace-pre-wrap">{{ t("chat.queued", { text: message.text || t("chat.voiceNote") }) }}</p>
		</div>

		<ChatComposer
			ref="composerRef"
			:agent-code="agent.code"
			:streaming="streaming"
			:error="failure"
			@submit="submit"
			@queue="enqueue"
			@stop="stop"
		/>
	</div>
	<div v-if="previewOpen && splitPreview" role="separator" aria-orientation="vertical" :aria-label="t('document.heading')" class="group flex w-2.5 shrink-0 cursor-col-resize items-center justify-center" style="touch-action: none" @pointerdown="documentSplit.onDown" @pointermove="documentSplit.onMove" @pointerup="documentSplit.onUp" @pointercancel="documentSplit.onUp" @dblclick="documentSplit.onReset">
		<span class="h-9 w-[3px] rounded-full" :class="documentSplit.resizing.value ? 'bg-accent' : 'bg-border group-hover:bg-fg-subtle'" />
	</div>
	<!-- No telefone o cabeçalho da doca flutua por cima: sem reservar `--dock-top`, o X da prévia ficava debaixo dele. -->
	<ChatDocumentPanel v-if="previewOpen" class="min-w-0 border-l border-border pt-[var(--dock-top,0px)]" :class="splitPreview ? 'w-1/2 shrink-0 max-w-[calc(100%-320px)]' : 'flex-1'" :style="splitPreview ? documentSplit.style.value : undefined" :document="liveDocument" :file="previewFile" :path="previewPath" :revision="previewPath ? savedDocuments[previewPath] : undefined" @revise="reviseDocument" @close="closePreview" />
	</div>
</template>
